import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Disclaimer } from "@/components/shared";
import {
  Map, Coins, MessageCircleHeart, FolderClosed, GraduationCap, ListChecks,
  ArrowRight, Lock, HelpCircle, CheckCircle2,
} from "lucide-react";

const TOOLS = [
  {
    ic: Map, bg: "var(--green)", h: "Support roadmap", to: "/app/roadmap",
    what: "Your personalized map of programs, documents and next steps, built from your intake answers. It's the best place to start.",
    how: [
      "Open the roadmap to see recommended steps grouped into sections (first steps, funding, school, documents and more).",
      "Turn any recommendation into a task so it shows up in your reminders.",
      "It updates automatically when your family details change — refresh it any time from the roadmap page.",
    ],
  },
  {
    ic: Coins, bg: "var(--teal)", h: "Funding & tax credits", to: "/app/funding",
    what: "Guided help to understand the Disability Tax Credit (DTC), Child Disability Benefit (CDB) and Ontario supports that may fit your family.",
    how: [
      "Browse programs and use the filters to narrow by what matters to you.",
      "Open a program to see plain-language summaries, who may review it, and official application links.",
      "Save the ones you want to revisit — they'll appear on your dashboard.",
    ],
  },
  {
    ic: MessageCircleHeart, bg: "var(--grape)", h: "AI Family Navigator", to: "/app/navigator",
    what: "Plain-language help, any time, to prepare questions, checklists and school emails without the overwhelm.",
    how: [
      "Ask a question in your own words — for example, “Help me write an email to request an IEP meeting.”",
      "The Navigator uses your family context to keep answers relevant to your situation.",
      "Use the suggested prompts if you're not sure where to begin.",
    ],
  },
  {
    ic: FolderClosed, bg: "var(--sky)", h: "Document tracker", to: "/app/documents", paid: true,
    what: "One calm binder for assessments, receipts, deadlines and forms — so nothing gets lost.",
    how: [
      "Upload PDFs, photos or documents and label them by category.",
      "Get an optional AI summary of a document so you can find what you need fast.",
      "Everything stays private to your account.",
    ],
  },
  {
    ic: GraduationCap, bg: "var(--sun)", h: "School supports", to: "/app/roadmap",
    what: "Prepare accommodation requests, questions and follow-ups for your next school meeting with confidence.",
    how: [
      "Find school-related steps inside your roadmap's School Support section.",
      "Use the AI Family Navigator to draft emails and meeting questions.",
      "Save prep items as tasks so you're ready before the meeting.",
    ],
  },
  {
    ic: ListChecks, bg: "var(--coral)", h: "Tasks & reminders", to: "/app/tasks", paid: true,
    what: "Gentle, low-pressure nudges so nothing slips — only what matters, when it matters.",
    how: [
      "Create tasks yourself or add them straight from a roadmap recommendation.",
      "Set due dates and priorities; mark them done as you go.",
      "Subscribe to the calendar feed or turn on weekly email reminders from your Account.",
    ],
  },
];

export default function ToolsGuide() {
  const { isPaid } = useAuth();
  useEffect(() => { document.title = "About your tools — KidPath"; }, []);

  return (
    <div className="kid-stagger" data-testid="tools-guide-page">
      <PageHeader
        eyebrow="Getting started"
        title="About your tools"
        subtitle="A quick tour of everything on your dashboard and how to make the most of each part."
      />

      <div className="space-y-5">
        {TOOLS.map((t, i) => {
          const locked = t.paid && !isPaid;
          return (
            <div key={i} className="kid-card p-6" data-testid="tools-guide-item">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0" style={{ background: t.bg }}>
                  <t.ic size={22} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-bold text-kid-navy">{t.h}</h3>
                    {t.paid && (
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${locked ? "bg-kid-sun/20 text-[#9a7b1e]" : "bg-kid-green/15 text-[#3f9d5c]"}`}>
                        {locked && <Lock size={11} />} Family Path
                      </span>
                    )}
                  </div>
                  <p className="text-kid-muted mt-1.5 leading-relaxed">{t.what}</p>

                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-wide text-kid-muted font-semibold mb-2">How to use it</div>
                    <ul className="space-y-2">
                      {t.how.map((step, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-kid-navy/90">
                          <CheckCircle2 size={16} className="text-kid-teal shrink-0 mt-0.5" />
                          <span>{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to={t.to} className="inline-flex items-center gap-1.5 text-kid-teal font-semibold text-sm mt-4 hover:gap-2.5 transition-all" data-testid="tools-guide-open">
                    Open {t.h.toLowerCase()} <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!isPaid && (
        <div className="kid-card p-6 mt-5 bg-kid-skytint/40 border-kid-teal/20" data-testid="tools-guide-upgrade">
          <div className="flex items-start gap-3">
            <HelpCircle size={20} className="text-kid-teal shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-kid-navy">Want the full toolkit?</h4>
              <p className="text-sm text-kid-muted mt-1">The Document tracker and Tasks & reminders are part of the Guided Support (Family Path) plan. Everything else is free to use as much as you like.</p>
              <Link to="/app/account" className="inline-flex items-center gap-1.5 text-kid-teal font-semibold text-sm mt-3" data-testid="tools-guide-upgrade-link">
                See plan options <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <Disclaimer>
          KidPath helps you understand and organize information and next steps. It doesn't determine eligibility or guarantee funding — a qualified professional or government decision-maker makes those decisions.
        </Disclaimer>
      </div>
    </div>
  );
}
